/** Small argument editors: single-field and checkbox-flavored templates.
 *  Wire shapes are dashboard-exact (see ruleCatalog.ts docstring). */
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  CHECKIN_SOURCE_OPTIONS,
  PARTICIPANT_SOURCE_OPTIONS,
  PRECHECKIN_STATUS_OPTIONS,
  SETTLEMENT_METHOD_OPTIONS,
  SURVEY_STATE_OPTIONS,
  TIME_UNIT_OPTIONS,
} from '../ruleCatalog';
import type { ArgEditorProps } from './common';
import { argsOf, BoolArg, CheckboxGroupArgs, MultiSelectArgs } from './common';

/** tpl 7 — `{period: <seconds>}`; empty = "logged in at all". The backend matcher
 *  does `timedelta(seconds=period)`, so a numeric STRING (what both editors used
 *  to write) raises at delivery — the rule-args schema now requires a number. */
export function LoggedInArgs(props: ArgEditorProps) {
  const period = argsOf(props.rule).period;
  return (
    <TextField
      size="small"
      type="number"
      label="Within the last (seconds)"
      sx={{ width: 260 }}
      value={period == null ? '' : String(period)}
      disabled={props.disabled}
      helperText="Leave empty to match anyone who has ever logged in"
      onChange={(e) => {
        const v = e.target.value;
        // The whole args object is {period: <number>} (null = logged in at all).
        props.onArgsChange({ period: v.trim() === '' || Number.isNaN(Number(v)) ? null : Number(v) });
      }}
    />
  );
}

/** tpl 32 — `{update_sources: ["0", …]}` (string codes). */
export function UpdateSourcesArgs(props: ArgEditorProps) {
  return <CheckboxGroupArgs {...props} argKey="update_sources" options={PARTICIPANT_SOURCE_OPTIONS} />;
}

/** tpl 35 — `{survey_state: ["0", …]}`. */
export function SurveyStateArgs(props: ArgEditorProps) {
  return <CheckboxGroupArgs {...props} argKey="survey_state" options={SURVEY_STATE_OPTIONS} />;
}

/** tpl 58 — `{sources: ["0", …]}`. */
export function CheckinSourceArgs(props: ArgEditorProps) {
  return <MultiSelectArgs {...props} argKey="sources" label="Check-in sources" options={CHECKIN_SOURCE_OPTIONS} />;
}

/** tpl 60 — `{precheckin_statuses: ["0", …]}`. */
export function PrecheckinStatusArgs(props: ArgEditorProps) {
  return (
    <MultiSelectArgs
      {...props}
      argKey="precheckin_statuses"
      label="Pre-check-in statuses"
      options={PRECHECKIN_STATUS_OPTIONS}
    />
  );
}

/** tpl 62 — `{settlement_method: ["DIRECT", …]}`. */
export function SettlementMethodArgs(props: ArgEditorProps) {
  return (
    <MultiSelectArgs
      {...props}
      argKey="settlement_method"
      label="Settlement methods"
      options={SETTLEMENT_METHOD_OPTIONS}
    />
  );
}

/** tpl 37 — `{rebooking: {from: bool, to: bool}}`. */
export function RebookingArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const rebooking = (args.rebooking ?? {}) as Record<string, unknown>;
  const toggle = (key: 'from' | 'to', checked: boolean) =>
    onArgsChange({ ...args, rebooking: { ...rebooking, [key]: checked } });
  return (
    <Stack direction="row" spacing={2}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(rebooking.from)}
            onChange={(e) => toggle('from', e.target.checked)}
            disabled={disabled}
          />
        }
        label="New booking (rebooked from another)"
      />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(rebooking.to)}
            onChange={(e) => toggle('to', e.target.checked)}
            disabled={disabled}
          />
        }
        label="Old booking (rebooked onto another)"
      />
    </Stack>
  );
}

/** tpl 40 — three independent booleans. */
export function ReviewPosNegArgs(props: ArgEditorProps) {
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <BoolArg {...props} argKey="no_review" label="No review" />
      <BoolArg {...props} argKey="positive_review" label="Positive review" />
      <BoolArg {...props} argKey="negative_review" label="Negative review" />
    </Stack>
  );
}

/** tpl 44 — `{low, high}` floats via a 0–5 half-star slider. */
export function ReviewRatingArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const low = typeof args.low === 'number' ? args.low : 0;
  const high = typeof args.high === 'number' ? args.high : 5;
  return (
    <Box sx={{ px: 1, maxWidth: 360 }}>
      <Typography variant="caption" color="text.secondary">
        Rating between {low.toFixed(1)} and {high.toFixed(1)}
      </Typography>
      <Slider
        size="small"
        min={0}
        max={5}
        step={0.5}
        value={[low, high]}
        onChange={(_, v) => {
          const [lo, hi] = v as number[];
          onArgsChange({ ...args, low: lo, high: hi });
        }}
        valueLabelDisplay="auto"
        disabled={disabled}
      />
    </Box>
  );
}

/** tpl 61 — `{email_receipt: bool}`. */
export function EmailReceiptArgs(props: ArgEditorProps) {
  return (
    <BoolArg
      {...props}
      argKey="email_receipt"
      label="Booking system permits sending the email receipt to the user"
    />
  );
}

/** tpl 66 — `{scope: 'current' | 'any'}`. */
export function SubbookingsScopeArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const scope = argsOf(rule).scope === 'current' ? 'current' : 'any';
  return (
    <TextField
      select
      size="small"
      label="Scope"
      sx={{ width: 300 }}
      value={scope}
      disabled={disabled}
      onChange={(e) => onArgsChange({ ...argsOf(rule), scope: e.target.value })}
    >
      <MenuItem value="current">Current tour participant only</MenuItem>
      <MenuItem value="any">Any tour participant in the booking</MenuItem>
    </TextField>
  );
}

/** tpls 39/42 — `{raw_delta: "<string>", unit, delta: raw×unit seconds}`.
 *  `delta` is what the matcher reads; raw_delta/unit are UI state the
 *  dashboard also persists — keep all three in sync. */
export function DeltaArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const rawDelta = args.raw_delta == null ? '' : String(args.raw_delta);
  const unit = Number(args.unit) || 3600;
  const apply = (nextRaw: string, nextUnit: number) => {
    const amount = Number(nextRaw);
    onArgsChange({
      ...args,
      raw_delta: nextRaw,
      unit: nextUnit,
      delta: Number.isFinite(amount) ? amount * nextUnit : 0,
    });
  };
  return (
    <Stack direction="row" spacing={1.5}>
      <TextField
        size="small"
        type="number"
        label="Within"
        sx={{ width: 140 }}
        value={rawDelta}
        disabled={disabled}
        onChange={(e) => apply(e.target.value, unit)}
      />
      <TextField
        select
        size="small"
        label="Unit"
        sx={{ width: 140 }}
        value={unit}
        disabled={disabled}
        onChange={(e) => apply(rawDelta, Number(e.target.value))}
      >
        {TIME_UNIT_OPTIONS.map((u) => (
          <MenuItem key={u.value} value={u.value}>
            {u.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

/** tpl 41 — location radius (backend matcher is a stub; kept for parity). */
export function LocationRadiusArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const text = (key: string, label: string) => (
    <TextField
      size="small"
      type="number"
      label={label}
      sx={{ width: 140 }}
      value={args[key] == null ? '' : String(args[key])}
      disabled={disabled}
      onChange={(e) => onArgsChange({ ...argsOf(rule), [key]: e.target.value })}
    />
  );
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
      {text('latitude', 'Latitude')}
      {text('longitude', 'Longitude')}
      {text('radius', 'Radius (m)')}
      <TextField
        select
        size="small"
        label="Locate via"
        sx={{ width: 150 }}
        value={args.fetch_method === 'ip_address' ? 'ip_address' : 'gps'}
        disabled={disabled}
        onChange={(e) => onArgsChange({ ...argsOf(rule), fetch_method: e.target.value })}
      >
        <MenuItem value="gps">GPS</MenuItem>
        <MenuItem value="ip_address">IP address</MenuItem>
      </TextField>
    </Stack>
  );
}
